import { isUserLogin } from '@eureka/utils/account';
import { createSharedComposable } from '@vueuse/core';
import Cookies from 'js-cookie';
import { useRoute } from 'vue-router';
import { useTracking } from '../tracking/use-tracking';

type AuthState = 'logged_in' | 'guest';
type FormulationStage = 'intent' | 'search' | 'extract' | 'formulate';

export type FormulationTrackingEventKey =
  | 'mse_formulation_click_example_prompt'
  | 'mse_formulation_start_from_landing'
  | 'mse_formulation_upload_file'
  | 'mse_formulation_enter_conversation'
  | 'mse_formulation_submit_prompt'
  | 'mse_formulation_stop_generation'
  | 'mse_formulation_share'
  | 'mse_formulation_feedback_submit'
  | 'mse_formulation_view_detail'
  | 'mse_formulation_toggle_ai_refine'
  | 'mse_formulation_toggle_row_lock'
  | 'mse_formulation_export'
  | 'mse_formulation_open_source_link'
  | 'mse_formulation_open_extract_source_detail';

type TrackMeta = {
  sourceType?: string;
  useLandingSourceType?: boolean;
  includeGuestSignature?: boolean;
  includeRouteScope?: boolean;
  includeDerivedStage?: boolean;
};

const FORMULATION_PAGE_SOURCE = 'MSE_FORMULATION_PAGE';

const getLandingSourceType = (authState: AuthState) => {
  return authState === 'logged_in' ? 'MSE_HOME_PAGE' : 'MSE_LANDING_PAGE';
};

const LANDING_EVENT_META: TrackMeta = {
  useLandingSourceType: true,
  includeGuestSignature: true,
};

const deriveStage = (toolKey: unknown): FormulationStage | undefined => {
  if (typeof toolKey !== 'string' || !toolKey) return undefined;
  if (toolKey === 'extract_report') return 'extract';
  if (toolKey === 'filtered_json_docs') return 'search';
  if (toolKey.startsWith('intent_')) return 'intent';
  if (toolKey.startsWith('search_')) return 'search';
  if (toolKey.startsWith('extract_')) return 'extract';
  if (toolKey.startsWith('formulate_')) return 'formulate';
  return undefined;
};

const FORMULATION_STAGE_META: TrackMeta = {
  sourceType: FORMULATION_PAGE_SOURCE,
  includeGuestSignature: true,
  includeRouteScope: true,
  includeDerivedStage: true,
};

const EVENT_META: Record<FormulationTrackingEventKey, TrackMeta> = {
  mse_formulation_click_example_prompt: LANDING_EVENT_META,
  mse_formulation_start_from_landing: LANDING_EVENT_META,
  mse_formulation_upload_file: LANDING_EVENT_META,
  mse_formulation_enter_conversation: {
    sourceType: FORMULATION_PAGE_SOURCE,
    includeGuestSignature: true,
    includeRouteScope: true,
  },
  mse_formulation_submit_prompt: {
    sourceType: FORMULATION_PAGE_SOURCE,
    includeGuestSignature: true,
    includeRouteScope: true,
  },
  mse_formulation_stop_generation: {
    sourceType: FORMULATION_PAGE_SOURCE,
    includeGuestSignature: true,
    includeRouteScope: true,
  },
  mse_formulation_share: {
    sourceType: FORMULATION_PAGE_SOURCE,
    includeGuestSignature: true,
    includeRouteScope: true,
  },
  mse_formulation_feedback_submit: {
    sourceType: FORMULATION_PAGE_SOURCE,
    includeGuestSignature: true,
  },
  mse_formulation_view_detail: FORMULATION_STAGE_META,
  mse_formulation_toggle_ai_refine: FORMULATION_STAGE_META,
  mse_formulation_toggle_row_lock: FORMULATION_STAGE_META,
  mse_formulation_export: FORMULATION_STAGE_META,
  mse_formulation_open_source_link: FORMULATION_STAGE_META,
  mse_formulation_open_extract_source_detail: FORMULATION_STAGE_META,
};

export const useFormulationTracking = createSharedComposable(() => {
  const route = useRoute();
  const { sendTracking } = useTracking();
  let authStatePromise: Promise<AuthState> | null = null;

  const getAuthState = async () => {
    authStatePromise ??= isUserLogin().then((loggedIn) => (loggedIn ? 'logged_in' : 'guest'));
    return authStatePromise;
  };

  const getRouteScope = () => {
    return route.path.startsWith('/shared') || route.name === 'GuestFormulationConversation'
      ? 'shared'
      : 'own';
  };

  const trackFormulation = async (
    eventKey: FormulationTrackingEventKey,
    detail: Record<string, unknown> = {},
  ) => {
    const meta = EVENT_META[eventKey];
    const authState = await getAuthState();
    const payload: Record<string, unknown> = {
      ...detail,
      auth_state: authState,
    };
    if (meta.includeRouteScope) {
      payload.route_scope = getRouteScope();
    }
    if (meta.includeDerivedStage && payload.stage === undefined) {
      const stage = deriveStage(payload.tool_key);
      if (stage) {
        payload.stage = stage;
      }
    }
    if (meta.includeGuestSignature && authState === 'guest') {
      const visitorId = Cookies.get('visitor_id');
      if (visitorId) {
        payload.signature_id = visitorId;
      }
    }
    sendTracking(
      eventKey,
      meta.useLandingSourceType ? getLandingSourceType(authState) : meta.sourceType,
      payload,
    );
  };

  return { trackFormulation };
});
